import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import isBetween from 'dayjs/plugin/isBetween'
import { css, html, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { fromEvent, merge, Subject, takeUntil } from 'rxjs'
import { auditTime, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators'
import { componentText, SchmancyElement } from '@schmancy/mixins/index'
import { virtualize } from '@schmancy/primitives/virtualizer'

dayjs.extend(localeData)
dayjs.extend(weekday)
dayjs.extend(isBetween)

/** Rendered height of one month grid — measured, not guessed. Feeds the
 *  virtualizer's range and offset math, which is meaningless if the estimate is
 *  a row height rather than a month height. */
const MONTH_HEIGHT_PX = 328

const UNBOUNDED_PAST_MONTHS = 1
const UNBOUNDED_FUTURE_MONTHS = 60
const MONTH_WINDOW_EXTENSION = 12

/** Longest month window still mounted whole. The decision is the window's
 *  LENGTH, never whether a bound happens to exist: `windowEnd()` falls back to
 *  `UNBOUNDED_FUTURE_MONTHS` whenever `max` is absent, so `min` alone — every
 *  "not in the past" picker there is — bounds nothing.
 *
 *  Windowing never mounts fewer than ~12 months (`first = k-5`, `last = k+5`,
 *  plus the second item a 320px port spans), so a threshold near it saves a
 *  couple of blocks and buys `scrollToMonth`'s async seed-and-mount
 *  path. 24 saves half of them, and holds both "months since the order was
 *  raised" pickers on one arm for two years of order age. */
const MAX_EAGER_MONTHS = 24

/** Maximum frames to wait for a virtualized destination to mount. */
const SCROLL_WAIT_FRAMES = 40

/** A bare `YYYY-MM-DD` — no time attached. */
const BARE_YMD = /^\d{4}-\d{2}-\d{2}$/

/** Fired on every provisional change (including range hover-preview). */
export type SchmancyCalendarInputEvent = CustomEvent<{
	value: string | null
	start: string | null
	end: string | null
}>

/** Fired on a committed change: single click | second range endpoint | clear. */
export type SchmancyCalendarChangeEvent = CustomEvent<{
	value: string | null
	start: string | null
	end: string | null
}>

/** Fired as the dominant in-view month changes while scrolling. */
export type SchmancyCalendarNavigateEvent = CustomEvent<{
	month: string
}>

/** Parse a 'YYYY-MM' string into { year, month } (1-based month). */
function parseYM(ym: string): { year: number; month: number } {
	const [y, m] = ym.split('-').map(Number)
	return { year: y, month: m }
}

function toYM(d: dayjs.Dayjs): string {
	return d.format('YYYY-MM')
}

function toYMD(d: dayjs.Dayjs): string {
	return d.format('YYYY-MM-DD')
}

/** Number of months between two 'YYYY-MM' strings (inclusive). */
function monthsBetween(from: string, to: string): number {
	const f = parseYM(from)
	const t = parseYM(to)
	return (t.year - f.year) * 12 + (t.month - f.month) + 1
}

function addMonths(ym: string, n: number): string {
	const { year, month } = parseYM(ym)
	const d = dayjs(new Date(year, month - 1, 1)).add(n, 'month')
	return toYM(d)
}

/**
 * Derive the first day of the week (0=Sun … 6=Sat) from a BCP-47 locale tag.
 * Both spellings report 1=Mon … 7=Sun; converted to 0-based Sun.
 *
 * Both are asked because engines disagree on which one exists — Chrome carries
 * only `getWeekInfo()`. Reading one alone yields `undefined` there, and every
 * locale then falls through to the Sunday default with nothing to say why: a
 * German calendar simply opens on a Sunday.
 */
function firstDayOfWeek(locale: string | undefined): number {
	if (!locale) return 0
	try {
		const intlLocale = new Intl.Locale(locale) as {
			weekInfo?: { firstDay?: number }
			getWeekInfo?: () => { firstDay?: number }
		}
		const firstDay = intlLocale.getWeekInfo?.().firstDay ?? intlLocale.weekInfo?.firstDay
		if (firstDay == null) return 0
		return firstDay === 7 ? 0 : firstDay
	} catch {
		return 0
	}
}

/** Presentational: continuous vertical month-scroll, single or range selection, no form-association. */
@customElement('schmancy-calendar')
export class SchmancyCalendar extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				/* The scroller is h-full; with no host height it collapses
				   (lit-virtualizer falls back to ~150px).

				   352px is one whole month block (label 48 + weekday header 24 +
				   six 40px week rows + 16 of padding = 328) plus 24 of the NEXT
				   month's own label band. That tail is the scroll affordance when
				   an opened month lands flush. A consumer that caps this host below 352 — both
				   picker panels do, at max-h-80 — trades that band for the height,
				   and is left with the platform's own scrollbar. */
				height: 22rem;
			}
		`,
	]

	@property({ reflect: true }) mode: 'single' | 'range' = 'single'
	@property() type: 'date' | 'datetime-local' = 'date'
	@property() value?: string | null
	@property() start?: string | null
	@property() end?: string | null
	@property() min?: string
	@property() max?: string
	@property() month?: string
	/** BCP-47 locale tag; week-start derived from it. Stored as `calLocale` to avoid conflicting with the base-class `locale` getter. */
	@property({ attribute: 'locale' }) calLocale?: string
	@property({ type: Boolean, reflect: true }) disabled = false
	@property({ type: Boolean, reflect: true }) readonly = false

	/** The month the scroller is currently anchored to ('YYYY-MM'). */
	@state() private anchorMonth: string = toYM(dayjs())
	@state() private pastMonths = UNBOUNDED_PAST_MONTHS
	@state() private futureMonths = UNBOUNDED_FUTURE_MONTHS
	private expandedScrollTop: number | null = null

	/** In range mode: the first click (pending second endpoint). */
	@state() private pendingStart: string | null = null

	/** In range mode: day the pointer is hovering over (for preview). */
	@state() private hoverDate: string | null = null

	/** The single day cell carrying `tabindex="0"` — the ARIA grid roving-tabindex pattern. */
	@state() private focusedDate: string = toYMD(dayjs())
	private focusDestination: string | null = null

	/** Emits the dominant month on scroll — fed by the DOM scroller's fromEvent
	 *  (via scrollerReady$) and any future direct pushes, merged into one
	 *  debounced pipeline. */
	private readonly scroll$ = new Subject<string>()

	/** Emits the scroller element once it exists in the DOM; switchMap converts
	 *  it into the fromEvent stream that feeds scroll$. */
	private readonly scrollerReady$ = new Subject<HTMLElement>()

	override connectedCallback(): void {
		super.connectedCallback()

		if (this.month) {
			// `month` may carry a full 'YYYY-MM-DD' — anchorMonth's invariant is 'YYYY-MM'.
			this.anchorMonth = this.month.substring(0, 7)
		} else if (this.mode === 'range') {
			if (this.start) this.anchorMonth = toYM(dayjs(this.start))
			else if (this.end) this.anchorMonth = toYM(dayjs(this.end))
			else this.anchorMonth = toYM(dayjs())
		} else {
			this.anchorMonth = this.value ? toYM(dayjs(this.value)) : toYM(dayjs())
		}

		this.focusedDate =
			this.mode === 'range' ? (this.start ?? this.end ?? toYMD(dayjs())) : (this.value ?? toYMD(dayjs()))

		// scroll$ (direct pushes) merged with scrollerReady$ switchMapped to the
		// DOM scroller's 'scroll' events, once the scroller exists.
		merge(
			this.scroll$,
			this.scrollerReady$.pipe(
				switchMap(scroller =>
					merge(fromEvent(scroller, 'scroll'), fromEvent(scroller, 'rangechange')).pipe(
						// Ahead of the map, because the map IS the cost: dominantMonth
						// forces a synchronous layout per month element, and the
						// `debounceTime` downstream bounds the emitted event, never this.
						auditTime(16),
						map(event => ({
							past:
								event.type === 'scroll' &&
								this.expandedScrollTop === null &&
								!this.min &&
								scroller.scrollTop < MONTH_HEIGHT_PX / 2
									? MONTH_WINDOW_EXTENSION
									: 0,
							future:
								event.type === 'scroll' &&
								this.expandedScrollTop === null &&
								!this.max &&
								scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < MONTH_HEIGHT_PX
									? MONTH_WINDOW_EXTENSION
									: 0,
						})),
						map(extension => ({
							...extension,
							scrollTop:
								extension.past || extension.future
									? scroller.scrollTop + extension.past * MONTH_HEIGHT_PX
									: this.expandedScrollTop,
						})),
						tap(extension => {
							this.expandedScrollTop = extension.scrollTop
							this.pastMonths += extension.past
							this.futureMonths += extension.future
						}),
						map(() => this.dominantMonth(scroller)),
						tap(month => {
							// Scrolling unmounts the month holding the roving tab stop, and the
							// grid then sits in no Tab order at all — with `moveFocusTo`
							// reachable only from a keydown on a cell that is already focused,
							// nothing recovers it. Re-seat it on the month in view.
							// A distant explicit scroll crosses intermediate virtual windows;
							// those must not replace the destination's pending tab stop.
							if (
								this.disabled ||
								this.focusDestination ||
								this.scrollFrame !== undefined ||
								scroller.querySelector('[role="gridcell"][tabindex="0"]')
							)
								return
							// The selected day when the month in view holds it — the grid's own answer
							// to "where am I" — and the month's first day otherwise.
							const selected = this.mode === 'single' && this.value ? toYMD(dayjs(this.value)) : null
							const inView =
								(selected?.startsWith(month)
									? scroller.querySelector<HTMLElement>(`[data-iso="${selected}"]`)
									: null) ?? scroller.querySelector<HTMLElement>(`[data-month="${month}"] [data-iso]`)
							if (inView?.dataset.iso) this.focusedDate = inView.dataset.iso
						}),
					),
				),
			),
		)
			.pipe(distinctUntilChanged(), debounceTime(50), takeUntil(this.disconnecting))
			.subscribe(month => {
				this.dispatchEvent(
					new CustomEvent<SchmancyCalendarNavigateEvent['detail']>('navigate', {
						detail: { month },
						bubbles: true,
						composed: true,
					}),
				)
			})
	}

	// From here and never `updated()`: `anchorMonth` is reactive, so a write
	// after render schedules a trailing update (Lit's `change-in-update`
	// warning); written here it folds into the update in flight, and the render
	// this cycle commits already anchors on the new month. The scroll targets
	// the previous render's scroller in either hook — the anchor-driven
	// re-render has not painted at the moment either would run — and on first
	// mount there is no scroller yet, which `updated()`'s attach block below
	// covers via its own `scrollToMonth(clampToWindow(resolveAnchor()))`.
	override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		// Answers every day cell used to recompute for itself, identical across the
		// pass. The formatter dominates: benchmarked per-cell against hoisted, it
		// is ~25x the rest of this block put together. Read here rather than in
		// `render()` so a handler reached between renders — `handleDayClick` asks
		// `isDisabled` — never reads a bound from before the property that moved it.
		this.todayYMD = toYMD(dayjs())
		this.dayNameFormat = new Intl.DateTimeFormat(this.effectiveLocale(), {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		})
		if (changed.has('min') || changed.has('max')) {
			const min = this.min ? dayjs(this.min) : null
			const max = this.max ? dayjs(this.max) : null
			// An unparseable bound disabled nothing before — every dayjs comparison
			// against an Invalid Date is false — and disables nothing now.
			this.minYMD = min?.isValid() ? toYMD(min) : null
			this.maxYMD = max?.isValid() ? toYMD(max) : null
		}
		// The `month` prop may carry a full date 'YYYY-MM-DD' — normalize to YYYY-MM.
		if (changed.has('month') && this.month) {
			const ym = this.month.substring(0, 7)
			if (ym !== this.anchorMonth) {
				this.anchorMonth = ym
				this.scrollToMonth(ym)
			}
		}
		// The anchor is otherwise resolved once, at mount, so a value written from
		// outside — a preset chip, a consumer's `.start=` — lands wherever it lands
		// and nothing brings the scroller to it. The invariant is "never leave the
		// whole selection off-screen", not "follow the value": a click on a visible
		// day and the far endpoint of a range swept across months each leave one end
		// on screen, and following those yanks the scroll out from under the pointer
		// that just set them. Per DAY, not per month — one month is 328px against a
		// ~320px scrollport, so a month can be "partly on screen" by a strip holding
		// none of the selected days. Read before this update renders, so it answers
		// for the layout on screen right now.
		if (this.hasUpdated && (changed.has('value') || changed.has('start') || changed.has('end'))) {
			const selected = (this.mode === 'range' ? [this.start, this.end] : [this.value]).filter((v): v is string => !!v)
			const scroller = this.shadowRoot?.querySelector('[part~="scroll"]') as HTMLElement | null
			const view = scroller?.getBoundingClientRect()
			const onScreen =
				!!scroller &&
				!!view &&
				selected.some(v => {
					const cell = scroller.querySelector<HTMLElement>(`[data-iso="${toYMD(dayjs(v))}"]`)?.getBoundingClientRect()
					return !!cell && cell.bottom > view.top && cell.top < view.bottom
				})
			if (selected.length > 0 && !onScreen) {
				this.anchorMonth = this.resolveAnchor()
				// A value `min`/`max` refuses has no month in the window to scroll to,
				// so the grid goes to the nearest one it may show — the bound's own.
				this.scrollToMonth(this.clampToWindow(this.anchorMonth))
			}
		}
		// The roving tab stop follows a single value written from outside — a date
		// typed into a picker's entry box. Left on the day it was seeded with, the
		// next Tab into the grid focuses that stale cell, the browser scrolls it
		// into view, and the grid snaps back to the month it showed before. Only
		// the tabindex moves; focus is never taken. A day outside the window (a
		// typed date beyond `min`/`max`) leaves the stop where it is.
		if (this.hasUpdated && this.mode === 'single' && changed.has('value') && this.value) {
			const day = dayjs(this.value)
			const ym = day.isValid() ? toYM(day) : null
			if (ym && ym >= this.windowStart() && ym <= this.windowEnd()) this.focusedDate = toYMD(day)
			// Out of bounds, the stop goes to the bound the grid moved to, so a Tab
			// into the grid does not drag it back to the month it left.
			else if (ym && ym < this.windowStart() && this.minYMD) this.focusedDate = this.minYMD
			else if (ym && ym > this.windowEnd() && this.maxYMD) this.focusedDate = this.maxYMD
		}
	}

	override updated(changed: PropertyValues): void {
		super.updated(changed)
		// Re-attaches on every scroller identity change, including the
		// virtualizer↔bounded-div branch switch.
		const scroller = this.shadowRoot?.querySelector('[part="scroll"]') as HTMLElement | null
		if (scroller && scroller !== this.attachedScroller) {
			this.attachedScroller = scroller
			// Re-emit on every identity change: switchMap drops the previous
			// fromEvent, so a stale listener is never left on a detached node.
			// Emitting only for the first scroller strands the subscription on
			// the element the branch switch replaced, and scroll-driven navigate
			// goes silent with no error.
			this.scrollerReady$.next(scroller)
			// Fires on first mount and on every branch switch, so the correct
			// scroller is always scrolled to the meaningful-date month — or, when
			// `min`/`max` leave that month out (today past a closed window), to the
			// nearest one the window renders.
			this.scrollToMonth(this.clampToWindow(this.resolveAnchor()))
		}
		if (scroller && this.expandedScrollTop !== null) {
			// The new scroll length must exist before assigning the preserved offset.
			// Re-render immediately so the virtualizer mounts that offset before paint,
			// including when extending a short max-only window changes its render arm.
			this.cancelScroll()
			scroller.scrollTop = this.expandedScrollTop
			this.expandedScrollTop = null
			this.requestUpdate()
		}
	}

	// The scroller scrollerReady$ last emitted, so a re-render that keeps the
	// same element does not re-subscribe.
	private attachedScroller: HTMLElement | null = null

	/** Today, the calendar day each bound resolves to, and the day-name formatter
	 *  — refreshed in `willUpdate`, read once per day cell. */
	private todayYMD = toYMD(dayjs())
	private minYMD: string | null = null
	private maxYMD: string | null = null
	private dayNameFormat = new Intl.DateTimeFormat(undefined, {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	})

	/** True when the window holds more months than are worth mounting whole. */
	private get useVirtualizer(): boolean {
		return this.months().length > MAX_EAGER_MONTHS
	}

	private windowStart(): string {
		if (this.min) return toYM(dayjs(this.min).startOf('month'))
		return addMonths(this.resolveAnchor(), -this.pastMonths)
	}

	private windowEnd(): string {
		if (this.max) return toYM(dayjs(this.max).endOf('month'))
		return addMonths(this.resolveAnchor(), this.futureMonths)
	}

	/** `ym` held inside the window's first and last month. */
	private clampToWindow(ym: string): string {
		const start = this.windowStart()
		const end = this.windowEnd()
		return ym < start ? start : ym > end ? end : ym
	}

	private resolveAnchor(): string {
		if (this.month) return this.month.substring(0, 7)
		if (this.mode === 'range') {
			if (this.start) return toYM(dayjs(this.start))
			if (this.end) return toYM(dayjs(this.end))
		} else {
			if (this.value) return toYM(dayjs(this.value))
		}
		return toYM(dayjs())
	}

	/** Reused while min/max/today are unchanged, so the virtualizer's `.items`
	 *  binding stays reference-equal and doesn't re-layout and reset scroll. */
	private monthsCache: string[] = []
	private monthsCacheKey = ''

	private months(): string[] {
		const start = this.windowStart()
		const end = this.windowEnd()
		const key = `${start}/${end}`
		if (key !== this.monthsCacheKey) {
			const count = monthsBetween(start, end)
			this.monthsCache = Array.from({ length: count }, (_, i) => addMonths(start, i))
			this.monthsCacheKey = key
		}
		return this.monthsCache
	}

	/** Explicit `locale` attribute wins; otherwise the theme-derived locale
	 *  (`BaseElement#locale`, already free — no new i18n API). */
	private effectiveLocale(): string {
		return this.calLocale ?? this.locale
	}

	/** Days of the week starting from the locale's first day, abbreviated via Intl. */
	private weekdayHeaders(): string[] {
		const locale = this.effectiveLocale()
		const firstDay = firstDayOfWeek(locale)
		const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' })
		// 2023-01-01 was a Sunday; UTC keeps the weekday stable regardless of host timezone.
		const base = Array.from({ length: 7 }, (_, i) => formatter.format(new Date(Date.UTC(2023, 0, 1 + i))))
		return [...base.slice(firstDay), ...base.slice(0, firstDay)]
	}

	private dominantMonth(scroller: HTMLElement): string {
		const monthEls = scroller.querySelectorAll<HTMLElement>('[data-month]')
		const scrollMid = scroller.scrollTop + scroller.clientHeight / 2
		let best: string = this.anchorMonth
		let bestDist = Infinity
		for (const el of monthEls) {
			const elMid = el.offsetTop + el.offsetHeight / 2
			const dist = Math.abs(elMid - scrollMid)
			if (dist < bestDist) {
				bestDist = dist
				best = el.dataset.month!
			}
		}
		return best
	}

	private scrollFrame: number | undefined

	private readonly scrollIntent = {
		capture: true,
		passive: true,
		handleEvent: () => this.cancelScroll(),
	}

	private cancelScroll(): void {
		if (this.scrollFrame !== undefined) cancelAnimationFrame(this.scrollFrame)
		this.scrollFrame = undefined
	}

	override disconnectedCallback(): void {
		this.cancelScroll()
		this.focusDestination = null
		super.disconnectedCallback()
	}

	/** Position an explicit destination once; subsequent scrolling belongs to the user. */
	private scrollToMonth(ym: string): void {
		this.cancelScroll()
		let seeded = false
		const apply = (waits: number): void => {
			this.scrollFrame = undefined
			if (!this.isConnected) return
			const scroller = this.shadowRoot?.querySelector<HTMLElement>('[part~="scroll"]')
			const target = scroller?.querySelector<HTMLElement>(`[data-month="${ym}"]`)
			if (scroller && target && scroller.clientHeight > 0) {
				// Client rects are post-transform: under an overlay's scale-in they
				// shrink by the scale, and a raw delta lands months short. Divide it
				// back out, read off the target month — its layout height is a whole
				// 328, where the scrollport's offsetHeight rounds a fractional height
				// and would skew the scale by the rounding.
				const port = scroller.getBoundingClientRect()
				const box = target.getBoundingClientRect().height
				const scale = box > 0 ? target.offsetHeight / box : 1
				scroller.scrollTop += (target.getBoundingClientRect().top - port.top) * scale
				return
			}
			// A distant destination must enter the virtualizer's window before it
			// can be measured. Seed once so waiting cannot fight a user's gesture.
			if (scroller && this.useVirtualizer && !seeded) {
				const index = this.months().indexOf(ym)
				if (index < 0) return
				scroller.scrollTop = index * MONTH_HEIGHT_PX
				seeded = true
			}
			if (waits < SCROLL_WAIT_FRAMES) this.scrollFrame = requestAnimationFrame(() => apply(waits + 1))
		}
		this.scrollFrame = requestAnimationFrame(() => apply(0))
	}

	/** A cell's `iso` is a bare `YYYY-MM-DD`; a `datetime-local` value carries a
	 *  time. Compared as calendar days, never as strings — `===` stops matching the
	 *  moment a time is attached, and every endpoint then renders as an ordinary
	 *  unselected day while the value underneath is set. */
	private isSameDay(iso: string, value: string | null | undefined): boolean {
		if (!value) return false
		// `iso` is a bare YMD this component generated, so against another bare YMD
		// the string comparison IS the day comparison; the dayjs pair below is for a
		// value carrying a time. One deliberate divergence: an out-of-range bare YMD
		// no longer rolls over, so `2026-02-31` highlights nothing rather than
		// March 3.
		if (BARE_YMD.test(value)) return iso === value
		return dayjs(iso).isSame(dayjs(value), 'day')
	}

	private isSelected(iso: string): boolean {
		if (this.mode === 'single') return this.isSameDay(iso, this.value)
		return this.isSameDay(iso, this.start) || this.isSameDay(iso, this.end)
	}

	private isRangeStart(iso: string): boolean {
		return this.mode === 'range' && this.isSameDay(iso, this.start)
	}

	private isRangeEnd(iso: string): boolean {
		return this.mode === 'range' && this.isSameDay(iso, this.end)
	}

	private isInRange(iso: string): boolean {
		if (this.mode !== 'range' || !this.start || !this.end) return false
		const d = dayjs(iso)
		const s = dayjs(this.start)
		const e = dayjs(this.end)
		const [lo, hi] = s.isBefore(e) ? [s, e] : [e, s]
		// Day granularity: at instant granularity a `…T23:59` end puts its own day
		// strictly inside the range, so that day carries the mid-range band and the
		// endpoint circle at once.
		return d.isAfter(lo, 'day') && d.isBefore(hi, 'day')
	}

	private isInPreview(iso: string): boolean {
		if (this.mode !== 'range' || !this.pendingStart || !this.hoverDate) return false
		const d = dayjs(iso)
		const s = dayjs(this.pendingStart)
		const h = dayjs(this.hoverDate)
		const [lo, hi] = s.isBefore(h) ? [s, h] : [h, s]
		return (d.isAfter(lo) && d.isBefore(hi)) || d.isSame(lo) || d.isSame(hi)
	}

	private isToday(iso: string): boolean {
		return iso === this.todayYMD
	}

	// min/max may carry a time component (datetime-local); a day is disabled only
	// when NO instant within it falls inside [min, max]. That granularity is the
	// day the bound falls on — `endOf('day') < min` holds exactly when iso's day
	// precedes min's own day — and both sides are zero-padded YMD, which orders
	// lexicographically, so the bound is parsed once per change in `willUpdate`.
	private isDisabled(iso: string): boolean {
		if (this.disabled) return true
		if (this.minYMD !== null && iso < this.minYMD) return true
		if (this.maxYMD !== null && iso > this.maxYMD) return true
		return false
	}

	private handleDayClick(iso: string): void {
		if (this.isDisabled(iso) || this.readonly) return

		this.focusedDate = iso

		if (this.mode === 'single') {
			this.value = iso
			this.emit('change', { value: iso, start: null, end: null })
			return
		}

		if (this.pendingStart === null) {
			this.pendingStart = iso
			this.hoverDate = iso
			this.emit('input', { value: null, start: iso, end: null })
		} else {
			const [s, e] = this.pendingStart <= iso ? [this.pendingStart, iso] : [iso, this.pendingStart]
			this.pendingStart = null
			this.hoverDate = null
			this.start = s
			this.end = e
			this.emit('change', { value: null, start: s, end: e })
		}
	}

	private handleDayHover(iso: string): void {
		if (this.mode !== 'range' || this.pendingStart === null) return
		this.hoverDate = iso
		const [s, e] = this.pendingStart <= iso ? [this.pendingStart, iso] : [iso, this.pendingStart]
		this.emit('input', { value: null, start: s, end: e })
	}

	// ARIA grid pattern: arrows move the roving-tabindex cell by day/week, Home/End
	// to the locale week's bounds, PageUp/PageDown by month; Enter/Space activates.
	private handleKeyDown(e: KeyboardEvent, iso: string): void {
		switch (e.key) {
			case 'Enter':
			case ' ':
				e.preventDefault()
				this.handleDayClick(iso)
				return
			case 'ArrowRight':
				e.preventDefault()
				this.moveFocusTo(toYMD(dayjs(iso).add(1, 'day')))
				return
			case 'ArrowLeft':
				e.preventDefault()
				this.moveFocusTo(toYMD(dayjs(iso).subtract(1, 'day')))
				return
			case 'ArrowDown':
				e.preventDefault()
				this.moveFocusTo(toYMD(dayjs(iso).add(7, 'day')))
				return
			case 'ArrowUp':
				e.preventDefault()
				this.moveFocusTo(toYMD(dayjs(iso).subtract(7, 'day')))
				return
			case 'Home':
				e.preventDefault()
				this.moveFocusTo(this.startOfWeek(iso))
				return
			case 'End':
				e.preventDefault()
				this.moveFocusTo(toYMD(dayjs(this.startOfWeek(iso)).add(6, 'day')))
				return
			case 'PageUp':
				e.preventDefault()
				this.moveFocusTo(toYMD(dayjs(iso).subtract(e.shiftKey ? 12 : 1, 'month')))
				return
			case 'PageDown':
				e.preventDefault()
				this.moveFocusTo(toYMD(dayjs(iso).add(e.shiftKey ? 12 : 1, 'month')))
				return
		}
	}

	/** Start (per locale week-start) of the week containing `iso`. */
	private startOfWeek(iso: string): string {
		const d = dayjs(iso)
		const offset = (d.day() - firstDayOfWeek(this.effectiveLocale()) + 7) % 7
		return toYMD(d.subtract(offset, 'day'))
	}

	/** Moves the roving-tabindex cell to `iso` (clamped only at explicit bounds),
	 *  extending the rendered window when necessary, then
	 *  focuses the cell once the virtualizer has mounted its month. */
	private moveFocusTo(iso: string): void {
		const requestedMonth = iso.substring(0, 7)
		if (!this.min && requestedMonth < this.windowStart()) {
			this.pastMonths = monthsBetween(requestedMonth, this.resolveAnchor()) + MONTH_WINDOW_EXTENSION
		}
		if (!this.max && requestedMonth > this.windowEnd()) {
			this.futureMonths = monthsBetween(this.resolveAnchor(), requestedMonth) + MONTH_WINDOW_EXTENSION
		}
		const winStart = this.windowStart()
		const winEnd = this.windowEnd()
		const targetYm = iso.substring(0, 7)

		let clamped = iso
		if (targetYm < winStart) {
			clamped = `${winStart}-01`
		} else if (targetYm > winEnd) {
			const { year, month } = parseYM(winEnd)
			const lastDay = dayjs(new Date(year, month - 1, 1)).daysInMonth()
			clamped = `${winEnd}-${String(lastDay).padStart(2, '0')}`
		}

		this.focusedDate = clamped
		this.focusDestination = clamped
		const clampedYm = clamped.substring(0, 7)
		const scroller = this.shadowRoot?.querySelector('[part~="scroll"]') as HTMLElement | null
		if (!scroller?.querySelector(`[data-month="${clampedYm}"]`)) {
			this.scrollToMonth(clampedYm)
		}

		const focusWhenReady = (tries = 0): void => {
			if (this.focusDestination !== clamped || !this.isConnected) return
			const target = this.shadowRoot
				?.querySelector('[part~="scroll"]')
				?.querySelector<HTMLElement>(`[data-iso="${clamped}"]`)
			if (target) {
				target.scrollIntoView({ block: 'nearest' })
				target.focus()
				this.focusDestination = null
				return
			}
			if (tries < 40) requestAnimationFrame(() => focusWhenReady(tries + 1))
			else this.focusDestination = null
		}
		requestAnimationFrame(() => focusWhenReady())
	}

	private emit(
		eventName: 'input' | 'change',
		detail: { value: string | null; start: string | null; end: string | null },
	): void {
		this.dispatchEvent(
			new CustomEvent(eventName, {
				detail,
				bubbles: true,
				composed: true,
			}),
		)
	}

	render() {
		const allMonths = this.months()
		const weekdays = this.weekdayHeaders()
		// One name for one grid — the two arms below differ only in how the months
		// are mounted (virtualized vs. all at once), never in what the grid is.
		const gridLabel = componentText('calendar.grid')

		return html`
			${when(
				this.useVirtualizer,
				() => html`
					<div
						part="scroll"
						class="relative overflow-y-auto h-full"
						@pointerdown=${this.scrollIntent}
						@wheel=${this.scrollIntent}
						@keydown=${this.scrollIntent}
						role="grid"
						aria-label=${gridLabel}
						aria-multiselectable="${this.mode === 'range' ? 'true' : 'false'}"
					>
						${virtualize(
							allMonths,
							ym => ym,
							ym => this.renderMonth(ym, weekdays),
							{
								// A virtualized item here is a whole month grid, not a row:
								// measured at 328px (six week rows + the weekday header).
								// The directive's 48px default is a row-sized guess, and at
								// ~7x too small every range and offset it computes is wrong —
								// harmless only while the directive was binding to the
								// document instead of this scroller and rendering everything.
								estimateSize: () => MONTH_HEIGHT_PX,
							},
						)}
					</div>
				`,
				() => html`
					<div
						part="scroll"
						class="relative overflow-y-auto h-full"
						@pointerdown=${this.scrollIntent}
						@wheel=${this.scrollIntent}
						@keydown=${this.scrollIntent}
						role="grid"
						aria-label=${gridLabel}
						aria-multiselectable="${this.mode === 'range' ? 'true' : 'false'}"
					>
						${repeat(
							allMonths,
							ym => ym,
							ym => this.renderMonth(ym, weekdays),
						)}
					</div>
				`,
			)}
			<div id="live-status" role="status" aria-live="polite" class="sr-only"></div>
		`
	}

	private renderMonth(ym: string, weekdays: string[]) {
		const { year, month } = parseYM(ym)
		const firstDay = dayjs(new Date(year, month - 1, 1))
		const daysInMonth = firstDay.daysInMonth()

		const locale = this.effectiveLocale()
		const localeFirstDay = firstDayOfWeek(locale)
		// firstDay.day() returns 0=Sun … 6=Sat; adjust for locale offset.
		const startOffset = (firstDay.day() - localeFirstDay + 7) % 7

		const monthLabel = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(firstDay.toDate())

		const cells: Array<{ iso: string | null; day: number | null }> = []
		for (let i = 0; i < startOffset; i++) cells.push({ iso: null, day: null })
		for (let d = 1; d <= daysInMonth; d++) {
			cells.push({ iso: `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`, day: d })
		}
		// Pad to a fixed 6-week (42-cell) grid: uniform item height keeps
		// the virtualizer's layout stable and month-index → scroll-offset exact,
		// which scrollToMonth's deterministic scroll depends on.
		while (cells.length < 42) cells.push({ iso: null, day: null })

		// 7-day rows for ARIA grid structure.
		const rows: Array<typeof cells> = []
		for (let r = 0; r < cells.length; r += 7) rows.push(cells.slice(r, r + 7))

		return html`
			<div data-month="${ym}" class="flex flex-col w-full">
				<div part="month-label" class="sticky top-0 z-20 bg-surface-default p-3 font-medium text-base text-surface-on">
					<slot name="month-label">${monthLabel}</slot>
				</div>

				<div role="row" part="weekday" class="grid grid-cols-7 gap-0 px-3">
					${weekdays.map(
						d =>
							html`<div role="columnheader" class="text-center text-xs font-medium text-surface-on-variant py-1">
								${d}
							</div>`,
					)}
				</div>

				<div part="grid" class="flex flex-col px-3 pb-4">
					${repeat(
						rows,
						(_, i) => `${ym}-row-${i}`,
						(row, ri) => {
							// A trailing 6th-week row padding out a 5-row month has every
							// cell aria-hidden — that leaves a role="row" with zero
							// accessible children (axe: aria-required-children). Drop the
							// row semantics entirely for a row with no real day in it.
							const hasDay = row.some(cell => cell.iso !== null)
							return html`
								<div role=${ifDefined(hasDay ? 'row' : undefined)} class="grid grid-cols-7">
									${repeat(
										row,
										(_, ci) => `${ym}-${ri * 7 + ci}`,
										cell => {
											if (!cell.iso) {
												return hasDay
													? html`<div role="gridcell" aria-hidden="true" class="h-10"></div>`
													: html`<div class="h-10"></div>`
											}
											return this.renderDay(cell.iso, cell.day!)
										},
									)}
								</div>
							`
						},
					)}
				</div>
			</div>
		`
	}

	private renderDay(iso: string, day: number) {
		const disabled = this.isDisabled(iso)
		const selected = this.isSelected(iso)
		const rangeStart = this.isRangeStart(iso)
		const rangeEnd = this.isRangeEnd(iso)
		const inRange = this.isInRange(iso)
		const inPreview = this.isInPreview(iso)
		const isToday = this.isToday(iso)
		const isEndpoint = rangeStart || rangeEnd
		const isSingleDayRange = rangeStart && rangeEnd

		// Ordered preview bounds — needed for half-strip direction.
		const [previewLo, previewHi] =
			this.pendingStart && this.hoverDate
				? this.pendingStart <= this.hoverDate
					? [this.pendingStart, this.hoverDate]
					: [this.hoverDate, this.pendingStart]
				: [null, null]
		const isPreviewStart = inPreview && iso === previewLo
		const isPreviewEnd = inPreview && iso === previewHi
		const isSingleDayPreview = isPreviewStart && isPreviewEnd

		// Filled circle covers pendingStart alone (no hover yet) and both endpoints
		// once the user is actively hovering to build a preview range.
		const isPendingEndpoint =
			this.mode === 'range' &&
			this.pendingStart !== null &&
			(iso === this.pendingStart || (this.hoverDate !== null && iso === this.hoverDate))
		const isFilledCircle = selected || isEndpoint || isPendingEndpoint

		// M3 tonal band connecting the two endpoint circles; hidden for a single-day range.
		const hasRangeStrip = !isSingleDayRange && (rangeStart || rangeEnd || inRange)
		const hasPreviewStrip = !isSingleDayPreview && inPreview
		const hasStrip = hasRangeStrip || hasPreviewStrip

		// Start cap → right half; end cap → left half; mid-cells → full width.
		const isStartCap = (rangeStart && !rangeEnd) || (isPreviewStart && !isPreviewEnd)
		const isEndCap = (rangeEnd && !rangeStart) || (isPreviewEnd && !isPreviewStart)

		const parts = [
			'day',
			selected ? 'day-selected' : '',
			rangeStart ? 'day-range-start' : '',
			rangeEnd ? 'day-range-end' : '',
			inRange ? 'day-in-range' : '',
			inPreview ? 'day-preview' : '',
			isToday ? 'day-today' : '',
			disabled ? 'day-disabled' : '',
		]
			.filter(Boolean)
			.join(' ')

		const stripClasses = classMap({
			absolute: true,
			'inset-y-0': true,
			'left-1/2': isStartCap,
			'left-0': !isStartCap,
			'right-1/2': isEndCap,
			'right-0': !isEndCap,
			'bg-primary-container': !hasPreviewStrip || hasRangeStrip,
			'bg-primary-container/40': hasPreviewStrip && !hasRangeStrip,
		})

		// Inner circle carries all per-day visual state. `aspect-square` (not a
		// fixed h-10 alongside w-10) ties height to width so a flex-shrunk circle
		// (narrow panel width forcing w-10 below 40px) stays round instead of
		// flattening into an oval — both axes shrink together.
		const circleClasses = classMap({
			'w-10': true,
			'aspect-square': true,
			'rounded-full': true,
			flex: true,
			'items-center': true,
			'justify-center': true,
			'text-sm': true,
			relative: true,
			'z-10': true,
			'transition-colors': true,
			'duration-100': true,
			'select-none': true,
			// Filled endpoint
			'bg-primary-default': isFilledCircle,
			'text-primary-on': isFilledCircle,
			// Committed in-range (solid strip bg → on-container text for contrast)
			'text-primary-on-container': inRange && !isEndpoint,
			// Today: thin M3-style outline when not filled or in-range
			'ring-1': isToday && !isFilledCircle && !inRange,
			'ring-primary-default': isToday && !isFilledCircle && !inRange,
			'text-primary-default': isToday && !isFilledCircle && !inRange,
			'font-semibold': isToday,
			// Default text for idle / preview cells
			'text-surface-on': !isFilledCircle && !(inRange && !isEndpoint) && !(isToday && !isFilledCircle && !inRange),
			// Hover tint for idle interactive cells
			'hover:bg-surface-containerHigh': !disabled && !isFilledCircle && !inRange,
			// Focus ring on the circle triggered by the outer cell's :focus-visible
			'group-focus-visible:ring-2': true,
			'group-focus-visible:ring-primary-default': true,
			// Disabled
			'opacity-[.38]': disabled,
			'cursor-not-allowed': disabled,
			'pointer-events-none': disabled,
		})

		const outerClasses = classMap({
			group: true,
			'scroll-mt-12': true,
			relative: true,
			'h-10': true,
			'w-full': true,
			flex: true,
			'items-center': true,
			'justify-center': true,
			'focus-visible:outline-none': true,
			'cursor-pointer': !disabled,
			'cursor-not-allowed': disabled,
		})

		// Human name + state, so a screen reader hears which committed endpoint
		// this cell is — never the raw ISO string. `data-iso` (below) carries the
		// machine key for click/hover/focus lookups instead.
		const humanDate = this.dayNameFormat.format(dayjs(iso).toDate())
		// The first click of a range pick has a visual cue (isFilledCircle,
		// above) but needs its own announced state too — otherwise a screen
		// reader hears only the plain date with no cue that a range is now
		// mid-pick and this cell is its start.
		const isPendingStart = this.mode === 'range' && this.pendingStart !== null && iso === this.pendingStart
		const dayLabel = isSingleDayRange
			? componentText('calendar.day.selected', { date: humanDate })
			: rangeStart
				? componentText('calendar.day.rangeStart', { date: humanDate })
				: rangeEnd
					? componentText('calendar.day.rangeEnd', { date: humanDate })
					: this.mode === 'single' && selected
						? componentText('calendar.day.selected', { date: humanDate })
						: isPendingStart
							? componentText('calendar.day.rangeStartPending', { date: humanDate })
							: isToday
								? componentText('calendar.day.today', { date: humanDate })
								: humanDate

		return html`
			<div
				part="${parts}"
				class="${outerClasses}"
				role="gridcell"
				data-iso="${iso}"
				aria-label=${dayLabel}
				aria-selected="${selected || isEndpoint || inRange ? 'true' : 'false'}"
				aria-disabled="${disabled ? 'true' : 'false'}"
				tabindex="${this.disabled ? '-1' : iso === this.focusedDate ? '0' : '-1'}"
				@click="${() => this.handleDayClick(iso)}"
				@mouseenter="${() => this.handleDayHover(iso)}"
				@keydown="${(e: KeyboardEvent) => this.handleKeyDown(e, iso)}"
			>
				${when(hasStrip, () => html`<div class="${stripClasses}"></div>`)}
				<div class="${circleClasses}">${day}</div>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-calendar': SchmancyCalendar
	}
}
